import { getApp } from 'firebase/app';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';

const db = () => getFirestore(getApp());

// ── Small notification sound ────────────────────────────────────────────────
// One shared AudioContext, unlocked on the first tap/keypress (browsers keep
// audio muted until the user has interacted with the page). Muting is a
// per-device choice stored in localStorage (smc_notif_sound = 'off').
const SOUND_KEY = 'smc_notif_sound';
let _ctx = null;
function audioCtx() {
  if (!_ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    _ctx = new AC();
  }
  return _ctx;
}
if (typeof window !== 'undefined') {
  const unlock = () => {
    try { const c = audioCtx(); if (c && c.state === 'suspended') c.resume(); } catch (_) {}
    window.removeEventListener('pointerdown', unlock);
    window.removeEventListener('keydown', unlock);
  };
  window.addEventListener('pointerdown', unlock, { passive: true });
  window.addEventListener('keydown', unlock);
}

export function notifSoundEnabled() {
  try { return localStorage.getItem(SOUND_KEY) !== 'off'; } catch (_) { return true; }
}
export function setNotifSoundEnabled(on) {
  try { localStorage.setItem(SOUND_KEY, on ? 'on' : 'off'); } catch (_) {}
  if (on) playNotificationSound(true);
}

let _lastPlayed = 0;
/** Soft two-note "ding" (~0.35 s). Rate-limited so a burst plays once. */
export function playNotificationSound(force = false) {
  if (!force && !notifSoundEnabled()) return;
  if (Date.now() - _lastPlayed < 1500) return;
  _lastPlayed = Date.now();
  try {
    const ctx = audioCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') ctx.resume();
    const now = ctx.currentTime;
    [[988, 0, 0.16], [1319, 0.09, 0.13]].forEach(([freq, offset, vol]) => {
      const osc = ctx.createOscillator();
      const env = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      osc.connect(env);
      env.connect(ctx.destination);
      const t = now + offset;
      env.gain.setValueAtTime(0, t);
      env.gain.linearRampToValueAtTime(vol, t + 0.012);
      env.gain.exponentialRampToValueAtTime(0.0008, t + 0.26);
      osc.start(t);
      osc.stop(t + 0.28);
    });
  } catch (_) {}
}

/**
 * Space-themed notification chime using Web Audio API.
 * A rising A-major arpeggio with reverb — short, beautiful, celestial.
 */
export function playSpaceChime() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const now = ctx.currentTime;

    // Master gain
    const master = ctx.createGain();
    master.gain.value = 0.62;
    master.connect(ctx.destination);

    // Delay-based reverb
    const delay = ctx.createDelay(0.5);
    const fb    = ctx.createGain();
    const wet   = ctx.createGain();
    delay.delayTime.value = 0.18;
    fb.gain.value  = 0.22;
    wet.gain.value = 0.32;
    master.connect(delay);
    delay.connect(fb);
    fb.connect(delay);
    delay.connect(wet);
    wet.connect(ctx.destination);

    // Three-note ascending chime: A4 → E5 → A5 (perfect octave/fifth — celestial, resolved)
    [[440, 0, 0.46], [659.25, 0.1, 0.38], [880, 0.2, 0.30]].forEach(([freq, offset, vol]) => {
      // Fundamental
      const osc = ctx.createOscillator();
      const env = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      osc.connect(env);
      env.connect(master);
      const t = now + offset;
      env.gain.setValueAtTime(0, t);
      env.gain.linearRampToValueAtTime(vol, t + 0.022);
      env.gain.exponentialRampToValueAtTime(0.001, t + 0.55);
      osc.start(t);
      osc.stop(t + 0.6);

      // Octave shimmer
      const osc2 = ctx.createOscillator();
      const env2 = ctx.createGain();
      osc2.type = 'sine';
      osc2.frequency.value = freq * 2;
      osc2.connect(env2);
      env2.connect(master);
      env2.gain.setValueAtTime(0, t);
      env2.gain.linearRampToValueAtTime(vol * 0.11, t + 0.022);
      env2.gain.exponentialRampToValueAtTime(0.001, t + 0.32);
      osc2.start(t);
      osc2.stop(t + 0.38);
    });

    setTimeout(() => { try { ctx.close(); } catch (_) {} }, 2500);
  } catch (_) {}
}

/**
 * Creates a notification document in Firestore.
 * Skips if recipientId === fromId (no self-notify).
 */
export async function createNotification({ recipientId, type = 'mention', title, body, fromId, fromName, link = '' }) {
  if (!recipientId || recipientId === fromId) return;
  try {
    await addDoc(collection(db(), 'notifications'), {
      recipientId, type, title, body, fromId, fromName, link,
      read: false,
      createdAt: serverTimestamp(),
    });
  } catch (_) {}
}

/**
 * Extracts @mentioned members from text.
 * Returns array of member objects.
 */
export function extractMentions(text, members) {
  const found = [];
  const regex = /@(\S+)/g;
  let m;
  while ((m = regex.exec(text)) !== null) {
    const word = m[1].replace(/[^a-zA-Z]/g, '').toLowerCase();
    if (!word) continue;
    const member = members.find(mem =>
      (mem.name || mem.email || '').replace(/[^a-zA-Z]/g, '').toLowerCase().startsWith(word)
    );
    if (member && !found.find(f => f.uid === member.uid)) {
      found.push(member);
    }
  }
  return found;
}
